'use client';

import { createClient } from '@/lib/supabase/client';

interface EnquiryData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  source: 'contact_form' | 'service_footer' | 'landing_page';
  business_type?: string;
  offer_interest?: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export const enquiryService = {
  async submitEnquiry(enquiryData: EnquiryData) {
    const supabase = createClient();

    try {
      // Insert enquiry into database
      const { data, error } = await supabase
        .from('enquiries')
        .insert({
          name: enquiryData.name,
          email: enquiryData.email,
          phone: enquiryData.phone,
          service: enquiryData.service,
          message: enquiryData.message,
          source: enquiryData.source,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          throw new Error('Failed to submit enquiry. Please try again.');
        }
      }

      // Send email notification via edge function
      try {
        const { error: emailError } = await supabase.functions.invoke('send-enquiry-email', {
          body: enquiryData,
        });

        if (emailError) {
          console.log('Email notification error:', emailError.message);
          // Don't throw - enquiry was saved successfully
        }
      } catch (emailError) {
        console.log('Email notification failed:', emailError);
        // Don't throw - enquiry was saved successfully
      }

      return data;
    } catch (error: any) {
      if (isSchemaError(error)) {
        console.log('Schema-related error:', error.message);
        throw error;
      }
      throw error;
    }
  },

  async getAllEnquiries() {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data || [];
    } catch (error: any) {
      if (isSchemaError(error)) {
        console.log('Schema-related error:', error.message);
        throw error;
      }
      return [];
    }
  },
};